import { BACKEND_URL as GREENWICH_BACKEND_URL } from '../utils/network.js';
import { MARKET } from '../utils/market.js';
// AuthContext.jsx
import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  useCallback,
} from "react";

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  // ✅ ՈՒՂԻՂ ՀԱՍՑԵ - ՍԱ ԿԱՇԽԱՏԵՆԹԵ AND PRODUCTION-ՈՒՄ
  const API_URL = `${GREENWICH_BACKEND_URL}`;
  const API_BASE_URL = API_URL;
  
  console.log("🚀 AuthProvider initialized");
  console.log("✅ Production Backend URL:", API_URL);
  console.log("✅ Environment:", 'production');

  const STORAGE_KEYS = {
    TOKEN: "token",
    USER: "user",
    TOKEN_EXPIRY: "token_expiry",
  };

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [redirectPath, setRedirectPath] = useState(null);
  const [serverOnline, setServerOnline] = useState(true);
  const [error, setError] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  
  // ✅ FOLLOW STATE
  const [following, setFollowing] = useState([]);
  const [followers, setFollowers] = useState([]);
  const [followRequests, setFollowRequests] = useState([]);
  const [suggestedUsers, setSuggestedUsers] = useState([]);
  
  const [userCommunities, setUserCommunities] = useState({
    createdGroups: [],
    memberGroups: [],
    pendingRequests: []
  });

  const isPrimaryAdminUser = useCallback((candidate) => candidate?.role === "admin", []);
  const isSecondaryAdminUser = useCallback(
    (candidate) => candidate?.role === "secondary_admin" && candidate?.secondaryAdminApproved === true,
    []
  );
  const hasAdminPermissionForUser = useCallback((candidate, permission) => {
    if (isPrimaryAdminUser(candidate)) return true;
    if (!isSecondaryAdminUser(candidate)) return false;
    const permissions = Array.isArray(candidate?.adminPermissions) ? candidate.adminPermissions : [];
    return permissions.includes(permission);
  }, [isPrimaryAdminUser, isSecondaryAdminUser]);

  // ==================== TOKEN FUNCTIONS ====================
  const getToken = () => {
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const expiry = localStorage.getItem(STORAGE_KEYS.TOKEN_EXPIRY);
    
    if (token && expiry) {
      const now = new Date().getTime();
      if (now > parseInt(expiry)) {
        localStorage.removeItem(STORAGE_KEYS.TOKEN);
        localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
        localStorage.removeItem(STORAGE_KEYS.USER);
        setTokenValid(false);
        return null;
      }
    }
    
    return token;
  };

  const setToken = (token, expiresIn = 86400) => {
    if (token) {
      localStorage.setItem(STORAGE_KEYS.TOKEN, token);
      const expiryTime = new Date().getTime() + (expiresIn * 1000);
      localStorage.setItem(STORAGE_KEYS.TOKEN_EXPIRY, expiryTime.toString());
      setTokenValid(true);
    } else {
      localStorage.removeItem(STORAGE_KEYS.TOKEN);
      localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
      setTokenValid(false);
    }
  };

  const clearToken = () => {
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.TOKEN_EXPIRY);
    localStorage.removeItem(STORAGE_KEYS.USER);
    setTokenValid(false);
  };

  // ==================== USER STATE FUNCTIONS ====================
  const updateUserState = useCallback((userData) => {
    if (!userData) {
      setUser(null);
      localStorage.removeItem(STORAGE_KEYS.USER);
      setUserCommunities({
        createdGroups: [],
        memberGroups: [],
        pendingRequests: []
      });
      setFollowing([]);
      setFollowers([]);
      setFollowRequests([]);
      setSuggestedUsers([]);
      setTokenValid(false);
      return;
    }

    setUser(userData);
    localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(userData));
    setTokenValid(true);

    window.dispatchEvent(
      new CustomEvent("globalUserProfileUpdated", {
        detail: { user: userData },
      })
    );
  }, []);

  // ==================== API CALL FUNCTION ====================
  const makeApiCall = async (endpoint, options = {}, isFormData = false) => {
    try {
      let token = getToken();
      
      const headers = {
        ...(options.headers || {}),
      };

      if (!isFormData && !headers["Content-Type"]) {
        headers["Content-Type"] = "application/json";
      }

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      }

      let body = options.body;
      if (!isFormData && body && headers["Content-Type"] === "application/json") {
        body = JSON.stringify(body);
      }

      // Remove leading slash if endpoint already has it
      let finalEndpoint = endpoint;
      if (finalEndpoint.startsWith(API_BASE_URL)) {
        finalEndpoint = finalEndpoint.replace(API_BASE_URL, '');
      }
      if (!finalEndpoint.startsWith('/')) {
        finalEndpoint = '/' + finalEndpoint;
      }

      const url = `${API_BASE_URL}${finalEndpoint}`;
      
      console.log(`🌐 API Call to PRODUCTION: ${options.method || 'GET'} ${url}`);
      
      const response = await fetch(url, {
        method: options.method || 'GET',
        headers,
        credentials: "include",
        body,
      });

      if (response.status === 401) {
        clearToken();
        updateUserState(null);
        window.dispatchEvent(new CustomEvent("tokenExpired"));
        
        return { 
          success: false, 
          error: "Session expired. Please login again.", 
          status: 401 
        };
      }

      let data;
      try {
        const text = await response.text();
        data = text ? JSON.parse(text) : {};
      } catch (e) {
        data = { error: "Invalid JSON response" };
      }

      if (!response.ok) {
        const errorMsg = data?.error || data?.message || `HTTP ${response.status}`;
        console.error(`❌ API Error (${response.status}): ${errorMsg}`);
        console.error("📄 Error details:", data);
        setError(errorMsg);
        return {
          success: false,
          error: errorMsg,
          status: response.status,
          data
        };
      }

      setError(null);
      return { 
        success: true, 
        ...data,
        status: response.status
      };
    } catch (error) {
      const errorMsg = error.message.includes('Failed to fetch') 
        ? `Cannot connect to PRODUCTION server at ${API_BASE_URL}. Please try again later.`
        : error.message || "Network error";
      
      console.error(`❌ Network Error: ${errorMsg}`);
      setError(errorMsg);
      setServerOnline(false);
      
      return {
        success: false,
        error: errorMsg,
        status: 0
      };
    }
  };

  const hydrateAuthenticatedSession = async (authUser, token, redirectTo = null) => {
    if (token) {
      setToken(token, 86400);
    }

    if (authUser) {
      updateUserState(authUser);
    }

    await loadUserCommunities();
    await loadFollowing();
    await loadFollowers();
    await loadFollowRequests();
    await loadSuggestedUsers();

    if (redirectTo) {
      setRedirectPath(redirectTo);
    }
  };

  // ==================== FOLLOW SYSTEM FUNCTIONS ====================
  const loadFollowing = async () => {
    try {
      const res = await makeApiCall("/api/users/following");
      
      if (res.success) {
        const followingList = res.following || res.data || [];
        console.log(`✅ Loaded following: ${followingList.length} users`);
        setFollowing(followingList);
        return { success: true, following: followingList };
      }
      console.error("❌ Failed to load following:", res.error);
      return { success: false, error: res.error, following: [] };
    } catch (error) {
      console.error("❌ Error loading following:", error);
      setFollowing([]);
      return { success: false, error: error.message, following: [] };
    }
  };

  const loadFollowers = async () => {
    try {
      const res = await makeApiCall("/api/users/followers");
      
      if (res.success) {
        const followersList = res.followers || res.data || [];
        console.log(`✅ Loaded followers: ${followersList.length} users`);
        setFollowers(followersList);
        return { success: true, followers: followersList };
      }
      console.error("❌ Failed to load followers:", res.error);
      return { success: false, error: res.error, followers: [] };
    } catch (error) {
      console.error("❌ Error loading followers:", error);
      setFollowers([]);
      return { success: false, error: error.message, followers: [] };
    }
  };

  const loadFollowRequests = async () => {
    try {
      const res = await makeApiCall("/api/users/follow-requests");
      
      if (res.success) {
        const requestsList = res.requests || res.data || [];
        console.log(`✅ Loaded follow requests: ${requestsList.length} requests`);
        setFollowRequests(requestsList);
        return { success: true, requests: requestsList };
      }
      console.error("❌ Failed to load follow requests:", res.error);
      return { success: false, error: res.error, requests: [] };
    } catch (error) {
      console.error("❌ Error loading follow requests:", error);
      setFollowRequests([]);
      return { success: false, error: error.message, requests: [] };
    }
  };

  const loadSuggestedUsers = async () => {
    try {
      const res = await makeApiCall("/api/users/suggested");
      
      if (res.success) {
        const suggestedList = res.users || res.suggested || res.data || [];
        console.log(`✅ Loaded suggested users: ${suggestedList.length} users`);
        setSuggestedUsers(suggestedList);
        return { success: true, suggested: suggestedList };
      }
      console.error("❌ Failed to load suggested users:", res.error);
      return { success: false, error: res.error, suggested: [] };
    } catch (error) {
      console.error("❌ Error loading suggested users:", error);
      setSuggestedUsers([]);
      return { success: false, error: error.message, suggested: [] };
    }
  };

  const adminGetDashboardStats = async (market) => {
    try {
      const qs = market && market !== "ALL" ? `?market=${encodeURIComponent(market)}` : "";
      const res = await makeApiCall(`/api/admin/dashboard-stats${qs}`);
      if (res.success) {
        return {
          success: true,
          stats: res.stats || {},
          recentRegistrations: res.recentRegistrations || [],
          market: res.market || "ALL",
          availableMarkets: res.availableMarkets || ["AM", "FR"],
        };
      }
      console.error("❌ Failed to load admin dashboard stats:", res.error);
      return { success: false, error: res.error, stats: {}, recentRegistrations: [] };
    } catch (error) {
      console.error("❌ Error loading admin dashboard stats:", error);
      return { success: false, error: error.message, stats: {}, recentRegistrations: [] };
    }
  };

  const sendFollowRequest = async (targetUserId) => {
    try {
      if (!targetUserId) {
        return { success: false, error: "User ID is required" };
      }
      
      const res = await makeApiCall(`/api/follow/request/${targetUserId}`, {
        method: "POST"
      });
      
      if (res.success) {
        setSuggestedUsers(prev => prev.filter(u => u._id !== targetUserId));
        setFollowRequests(prev => [...prev, { 
          _id: res.requestId || `request_${Date.now()}`, 
          targetUserId,
          status: 'pending',
          createdAt: new Date()
        }]);
        console.log(`✅ Follow request sent to user ${targetUserId}`);
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error sending follow request:", error);
      return { success: false, error: error.message };
    }
  };

  const acceptFollowRequest = async (requestId) => {
    try {
      if (!requestId) {
        return { success: false, error: "Request ID is required" };
      }
      
      const res = await makeApiCall(`/api/follow/accept/${requestId}`, {
        method: "PUT"
      });
      
      if (res.success) {
        const requestToAccept = followRequests.find(r => r._id === requestId);
        if (requestToAccept) {
          const userId = requestToAccept.fromUser?._id || requestToAccept.targetUserId;
          
          setFollowRequests(prev => prev.filter(r => r._id !== requestId));
          
          if (userId) {
            const userData = requestToAccept.fromUser || { _id: userId };
            setFollowers(prev => [...prev, userData]);
            setFollowing(prev => [...prev, userData]);
          }
        }
        console.log(`✅ Follow request ${requestId} accepted`);
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error accepting follow request:", error);
      return { success: false, error: error.message };
    }
  };

  const rejectFollowRequest = async (requestId) => {
    try {
      if (!requestId) {
        return { success: false, error: "Request ID is required" };
      }
      
      const res = await makeApiCall(`/api/follow/reject/${requestId}`, {
        method: "PUT"
      });
      
      if (res.success) {
        setFollowRequests(prev => prev.filter(r => r._id !== requestId));
        console.log(`✅ Follow request ${requestId} rejected`);
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error rejecting follow request:", error);
      return { success: false, error: error.message };
    }
  };

  const unfollowUser = async (userId) => {
    try {
      if (!userId) {
        return { success: false, error: "User ID is required" };
      }
      
      const res = await makeApiCall(`/api/unfollow/${userId}`, {
        method: "POST"
      });
      
      if (res.success) {
        setFollowing(prev => prev.filter(u => u._id !== userId));
        console.log(`✅ Unfollowed user ${userId}`);
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error unfollowing user:", error);
      return { success: false, error: error.message };
    }
  };

  const checkMutualFollow = async (targetUserId) => {
    try {
      if (!targetUserId) return false;
      
      const isFollowing = following.some(u => 
        u._id === targetUserId || u.user?._id === targetUserId
      );
      
      const isFollower = followers.some(f => 
        f._id === targetUserId || f.follower?._id === targetUserId
      );
      
      if (isFollowing && isFollower) {
        return true;
      }
      
      const res = await makeApiCall(`/api/follow/status/${targetUserId}`);
      if (res.success && res.status) {
        return res.status.isMutual || false;
      }
      
      return false;
    } catch (error) {
      console.error("❌ Error checking mutual follow:", error);
      return false;
    }
  };

  const loadAvailableUsersForMessages = async () => {
    try {
      console.log("🟡 Loading available users for messages...");
      
      const res = await makeApiCall("/api/users/available");
      
      if (res.success) {
        const availableUsers = res.users || res.data || [];
        console.log(`✅ Available users loaded: ${availableUsers.length}`);
        
        const mutualFollows = availableUsers.filter(userItem => {
          const userData = userItem.user || userItem;
          const userId = userData._id;
          
          if (!userId || userId === user?._id) return false;
          
          const isFollowing = following.some(f => 
            f._id === userId || 
            f.user?._id === userId || 
            f.followedUser?._id === userId
          );
          
          const isFollower = followers.some(f => 
            f._id === userId || 
            f.follower?._id === userId ||
            f.user?._id === userId
          );
          
          return isFollowing && isFollower;
        });
        
        console.log(`✅ Mutual follows found: ${mutualFollows.length}`);
        return { success: true, users: mutualFollows };
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error loading available users:", error);
      return { success: false, error: error.message, users: [] };
    }
  };

  const getUserById = async (userId) => {
    try {
      if (!userId) {
        return { success: false, error: "User ID is required" };
      }
      
      const res = await makeApiCall(`/api/users/${userId}/profile`);
      return res;
    } catch (error) {
      console.error("❌ Error getting user:", error);
      return { success: false, error: error.message };
    }
  };

  // ==================== MESSAGES FUNCTIONS ====================
  const loadConversations = async () => {
    try {
      console.log("🟡 Loading conversations...");
      const res = await makeApiCall("/api/conversations");
      
      if (res.success) {
        console.log(`✅ Conversations loaded: ${res.conversations?.length || 0}`);
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error loading conversations:", error);
      return { success: false, error: error.message };
    }
  };

  const createConversation = async (participantIds, isGroup = false) => {
    try {
      if (!participantIds || !Array.isArray(participantIds)) {
        return { success: false, error: "Participant IDs are required" };
      }
      
      console.log(`🟡 Creating conversation with participants: ${participantIds.join(', ')}`);
      const res = await makeApiCall("/api/conversations", {
        method: "POST",
        body: { participantIds, isGroup }
      });
      
      if (res.success) {
        console.log(`✅ Conversation created: ${res.conversation?._id}`);
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error creating conversation:", error);
      return { success: false, error: error.message };
    }
  };

  const loadConversationMessages = async (conversationId) => {
    try {
      if (!conversationId) {
        return { success: false, error: "Conversation ID is required" };
      }
      
      const res = await makeApiCall(`/api/conversations/${conversationId}/messages`);
      return res;
    } catch (error) {
      console.error("❌ Error loading conversation messages:", error);
      return { success: false, error: error.message };
    }
  };

  const sendMessage = async (conversationId, content, imageFile = null) => {
    try {
      if (!conversationId || (!content && !imageFile)) {
        return { success: false, error: "Conversation ID and content/image are required" };
      }
      
      if (imageFile) {
        const formData = new FormData();
        if (content) formData.append('content', content);
        formData.append('image', imageFile);
        
        const res = await makeApiCall(`/api/conversations/${conversationId}/messages`, {
          method: "POST",
          body: formData
        }, true);
        
        return res;
      } else {
        const res = await makeApiCall(`/api/conversations/${conversationId}/messages`, {
          method: "POST",
          body: { content }
        });
        
        return res;
      }
    } catch (error) {
      console.error("❌ Error sending message:", error);
      return { success: false, error: error.message };
    }
  };

  // ==================== POST FUNCTIONS ====================
  const loadFeedPosts = async (tab = 'all') => {
    try {
      const endpoint = tab === 'following' 
        ? "/api/feed/following" 
        : "/api/posts";
      
      console.log(`🟡 Loading ${tab} feed posts...`);
      const res = await makeApiCall(endpoint);
      
      if (res.success) {
        console.log(`✅ ${tab} posts loaded: ${res.posts?.length || 0}`);
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error loading feed posts:", error);
      return { success: false, error: error.message };
    }
  };

  const createPost = async (postData, imageFile = null) => {
    try {
      if (!postData?.content && !imageFile) {
        return {
          success: false,
          error: "Post content or image is required"
        };
      }
      
      const formData = new FormData();
      
      Object.keys(postData).forEach(key => {
        if (postData[key] !== null && postData[key] !== undefined) {
          formData.append(key, postData[key]);
        }
      });
      
      if (imageFile) {
        formData.append('image', imageFile);
      }
      
      console.log("🟡 Creating post...");
      const res = await makeApiCall("/api/posts", {
        method: "POST",
        body: formData
      }, true);
      
      if (res.success) {
        console.log(`✅ Post created: ${res.post?._id}`);
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error creating post:", error);
      return { success: false, error: error.message };
    }
  };

  const likePost = async (postId) => {
    try {
      if (!postId) {
        return { success: false, error: "Post ID is required" };
      }
      
      console.log(`🟡 Liking post ${postId}...`);
      const res = await makeApiCall(`/api/posts/${postId}/like`, {
        method: "POST"
      });
      
      return res;
    } catch (error) {
      console.error("❌ Error liking post:", error);
      return { success: false, error: error.message };
    }
  };

  const savePost = async (postId) => {
    try {
      if (!postId) {
        return { success: false, error: "Post ID is required" };
      }
      
      console.log(`🟡 Saving post ${postId}...`);
      const res = await makeApiCall(`/api/posts/${postId}/save`, {
        method: "POST"
      });
      
      return res;
    } catch (error) {
      console.error("❌ Error saving post:", error);
      return { success: false, error: error.message };
    }
  };

  const commentOnPost = async (postId, content) => {
    try {
      if (!postId || !content) {
        return { success: false, error: "Post ID and content are required" };
      }
      
      console.log(`🟡 Commenting on post ${postId}...`);
      const res = await makeApiCall(`/api/posts/${postId}/comments`, {
        method: "POST",
        body: { content }
      });
      
      return res;
    } catch (error) {
      console.error("❌ Error commenting on post:", error);
      return { success: false, error: error.message };
    }
  };

  // ==================== AUTH FUNCTIONS ====================
  const login = async (email, password) => {
    try {
      if (!email || !password) {
        return {
          success: false,
          error: "Email and password are required"
        };
      }
      
      console.log("🟡 Logging in...");
      const res = await makeApiCall("/api/auth/login", {
        method: "POST",
        body: { email, password },
      });

      console.log("Login response:", res);

      if (res.success && res.token && res.user) {
        console.log("✅ Login successful");
        const loginRedirect =
          res.user?.role === "admin"
            ? "/admin/dashboard"
            : res.user?.role === "secondary_admin"
              ? "/admin/programs"
              : "/feed";
        await hydrateAuthenticatedSession(res.user, res.token, loginRedirect);
      }

      // When res.data?.code === "EMAIL_NOT_VERIFIED", res is returned as-is (no
      // token/user) so the caller can send the user to the verification screen.
      return res;
    } catch (error) {
      console.error("❌ Login error:", error);
      return {
        success: false,
        error: error.message || "Login failed"
      };
    }
  };

  // Second step of login when /api/auth/login responded with { requires2FA: true, tempToken }
  // instead of a token — exchanges the ticket plus the user's authenticator (or backup) code
  // for the real session, same as a normal login from here on.
  const verifyTwoFactorLogin = async (tempToken, code) => {
    try {
      const res = await makeApiCall("/api/auth/2fa/verify-login", {
        method: "POST",
        body: { tempToken, code },
      });
      if (res.success && res.token && res.user) {
        const loginRedirect =
          res.user?.role === "admin"
            ? "/admin/dashboard"
            : res.user?.role === "secondary_admin"
              ? "/admin/programs"
              : "/feed";
        await hydrateAuthenticatedSession(res.user, res.token, loginRedirect);
      }
      return res;
    } catch (error) {
      console.error("❌ 2FA verify error:", error);
      return { success: false, error: error.message || "Invalid code" };
    }
  };

  const register = async (payload) => {
    try {
      if (!payload?.email || !payload?.password) {
        return {
          success: false,
          error: "Email and password are required"
        };
      }
      
      console.log("🟡 Registering...");
      const res = await makeApiCall("/api/auth/register", {
        method: "POST",
        body: payload,
      });

      console.log("Register response:", res);

      if (res.success && res.token && res.user) {
        console.log("✅ Registration successful");
        await hydrateAuthenticatedSession(
          res.user,
          res.token,
          payload.userType === "organizer" ? "/organization-account-profile" : "/feed"
        );
      }
      // res.requiresVerification: the account was created but needs the emailed code
      // before it can log in — the caller routes to the verification screen instead.

      return res;
    } catch (error) {
      console.error("❌ Registration error:", error);
      return {
        success: false,
        error: error.message || "Registration failed"
      };
    }
  };

  // ==================== ORGANIZATION REGISTRATION ====================
  // Organizers are created as pending; only admin can verify via /organizer-approvals.
  // No auto-approval — verified organizers then appear on /verified-organizations.
  const registerOrganization = async (payload) => {
    try {
      console.log("🏢 Organization registration started...");
      
      if (!payload?.stepFourData?.email) {
        return { success: false, error: "Email is required" };
      }
      
      if (!payload?.stepFourData?.password) {
        return { success: false, error: "Password is required" };
      }
      
      if (!payload?.stepFourData?.username) {
        return { success: false, error: "Username is required" };
      }
      
      // Create FormData
      const formData = new FormData();
      
      // Extract data from payload
      const stepFourData = payload.stepFourData || {};
      
      formData.append('email', stepFourData.email);
      formData.append('password', stepFourData.password);
      formData.append('username', stepFourData.username);
      formData.append('userType', 'organizer');
      formData.append('market', MARKET);
      if (payload.captchaToken) formData.append('captchaToken', payload.captchaToken);
      
      // Organization data as JSON
      const organizationData = {
        stepOneData: payload.stepOneData || {},
        stepTwoData: payload.stepTwoData || {},
        stepThreeData: payload.stepThreeData || {},
        stepFourData: {
          username: stepFourData.username,
          email: stepFourData.email,
          password: stepFourData.password,
          registrantRole: stepFourData.registrantRole || '',
          representative: stepFourData.representative || {},
        }
      };

      formData.append('organizationData', JSON.stringify(organizationData));

      const base64ToFile = (base64Data, fileName, fileType = 'application/pdf') => {
        if (!base64Data || typeof base64Data !== 'string') return null;
        
        try {
          if (base64Data.startsWith('data:')) {
            const base64Match = base64Data.match(/^data:(.+);base64,(.+)$/);
            if (!base64Match) return null;
            
            const mimeType = base64Match[1];
            const byteString = atob(base64Match[2]);
            const ab = new ArrayBuffer(byteString.length);
            const ia = new Uint8Array(ab);
            
            for (let i = 0; i < byteString.length; i++) {
              ia[i] = byteString.charCodeAt(i);
            }
            
            const blob = new Blob([ab], { type: mimeType });
            return new File([blob], fileName, { type: mimeType });
          }
          return null;
        } catch (error) {
          console.error("Error converting base64 to file:", error);
          return null;
        }
      };
      
      // Reeg.jsx (step 3) stores the org logo / presentation as base64 strings in
      // stepThreeData.documents — convert those into real files so they actually reach
      // the backend's `logo` / `orgPresentation` multer fields instead of only ever being
      // embedded (as inert base64 text) inside the JSON `organizationData` field above.
      const stepThreeDocuments = (payload.stepThreeData && payload.stepThreeData.documents) || {};
      if (stepThreeDocuments.logo) {
        const logoFile = base64ToFile(stepThreeDocuments.logo, `logo-${Date.now()}.jpg`, 'image/jpeg');
        if (logoFile) formData.append('logo', logoFile);
      }
      if (stepThreeDocuments.orgPresentation) {
        const presentationMime = stepThreeDocuments.orgPresentation.match(/^data:(.+);base64,/)?.[1] || 'application/pdf';
        const presentationName = stepThreeDocuments.orgPresentationName || `org-presentation-${Date.now()}`;
        const presentationFile = base64ToFile(stepThreeDocuments.orgPresentation, presentationName, presentationMime);
        if (presentationFile) formData.append('orgPresentation', presentationFile);
      }

      const res = await makeApiCall("/api/auth/organizations/register", {
        method: "POST",
        body: formData
      }, true);

      if (res.success) {
        console.log("✅ Organization registration successful!");
        // Only hydrate (auto-login) once a token comes back — while requiresVerification
        // is set there's no token yet, and the caller routes to the code-entry screen.
        if (res.token) {
          await hydrateAuthenticatedSession(res.organization || res.user, res.token, "/home");
        }

        return { success: true, ...res };
      } else {
        console.error("❌ Registration failed:", res.error);

        // Previously this branch faked a success response (and cleared the onboarding
        // sessionStorage) when the email was already taken, so the user believed they'd
        // registered while no account was actually created — they'd be stuck unable to log
        // in with no idea why. Surface the real error instead so they can use a different
        // email or log in with the existing account.
        return {
          success: false,
          error: res.error || "Registration failed",
        };
      }
    } catch (error) {
      console.error("❌ Organization registration error:", error);
      return {
        success: false,
        error: error.message || "Organization registration failed",
      };
    }
  };

  // ==================== TEAM INVITES ====================
  // Sends an email invitation to a prospective team member. They only join the
  // organization's confirmed team list once they accept it from the emailed link — see
  // /api/organizations/team/invite/:token/respond and TeamInviteConfirm.jsx.
  const inviteTeamMember = async ({ fullName, role, email }) => {
    try {
      const res = await makeApiCall("/api/organizations/team/invite", {
        method: "POST",
        body: { fullName, role, email },
      });
      return res.success
        ? { success: true, teamInvites: res.teamInvites }
        : { success: false, error: res.error || "Failed to send invite" };
    } catch (error) {
      return { success: false, error: error.message || "Failed to send invite" };
    }
  };

  // ==================== EMAIL VERIFICATION ====================
  const verifyEmail = async (email, code) => {
    try {
      const res = await makeApiCall("/api/auth/verify-email", {
        method: "POST",
        body: { email, code },
      });

      if (res.success && res.token && res.user) {
        // Verifying the code finishes registration (organizers land on their profile
        // setup, players continue into the interests step, same as a fresh signup did
        // before email verification existed).
        await hydrateAuthenticatedSession(
          res.user,
          res.token,
          res.user?.userType === "organizer" ? "/organization-account-profile" : "/player-interests"
        );
      }

      return res;
    } catch (error) {
      console.error("❌ Email verification error:", error);
      return { success: false, error: error.message || "Verification failed" };
    }
  };

  const resendVerificationCode = async (email) => {
    try {
      return await makeApiCall("/api/auth/resend-verification", {
        method: "POST",
        body: { email },
      });
    } catch (error) {
      console.error("❌ Resend verification code error:", error);
      return { success: false, error: error.message || "Could not resend code" };
    }
  };

  const socialAuth = async ({
    provider,
    accessToken,
    mode = "login",
    userType = "player",
    market = MARKET,
    organizationData = null,
    phone = "",
    redirectTo = null,
  }) => {
    try {
      if (!provider || !accessToken) {
        return {
          success: false,
          error: "Social provider and access token are required",
        };
      }

      const res = await makeApiCall("/api/auth/social", {
        method: "POST",
        body: {
          provider,
          accessToken,
          mode,
          userType,
          market,
          organizationData,
          phone,
        },
      });

      if (res.success && res.token && res.user) {
        const fallbackRedirect = redirectTo || (
          mode === "register"
            ? (userType === "organizer" ? "/home" : "/player-interests")
            : "/feed"
        );

        await hydrateAuthenticatedSession(res.user, res.token, fallbackRedirect);
      }

      return res;
    } catch (error) {
      console.error("❌ Social auth error:", error);
      return {
        success: false,
        error: error.message || "Social authentication failed",
      };
    }
  };

  const logout = () => {
    console.log("🔐 Logging out...");
    setToken(null);
    updateUserState(null);
    setUserCommunities({
      createdGroups: [],
      memberGroups: [],
      pendingRequests: []
    });
    setFollowing([]);
    setFollowers([]);
    setFollowRequests([]);
    setSuggestedUsers([]);
    setRedirectPath(null);
    setError(null);
    window.dispatchEvent(new CustomEvent("userLoggedOut"));
    console.log("✅ Logout complete");
  };

  const getSecondaryAdmins = async () => {
    try {
      return await makeApiCall("/api/admin/secondary-admins");
    } catch (error) {
      return { success: false, error: error.message || "Failed to load secondary admins" };
    }
  };

  const createSecondaryAdmin = async (payload) => {
    try {
      return await makeApiCall("/api/admin/secondary-admins", {
        method: "POST",
        body: payload
      });
    } catch (error) {
      return { success: false, error: error.message || "Failed to create secondary admin" };
    }
  };

  const approveSecondaryAdminAccess = async (userId) => {
    try {
      return await makeApiCall(`/api/admin/secondary-admins/${userId}/approve-access`, {
        method: "POST"
      });
    } catch (error) {
      return { success: false, error: error.message || "Failed to approve access" };
    }
  };

  const rejectSecondaryAdminAccess = async (userId) => {
    try {
      return await makeApiCall(`/api/admin/secondary-admins/${userId}/reject-access`, {
        method: "POST"
      });
    } catch (error) {
      return { success: false, error: error.message || "Failed to reject access" };
    }
  };

  const deleteSecondaryAdmin = async (userId) => {
    try {
      return await makeApiCall(`/api/admin/secondary-admins/${userId}`, {
        method: "DELETE"
      });
    } catch (error) {
      return { success: false, error: error.message || "Failed to delete secondary admin" };
    }
  };

  const updateSecondaryAdminPermissions = async (userId, permissions) => {
    try {
      return await makeApiCall(`/api/admin/secondary-admins/${userId}/permissions`, {
        method: "PATCH",
        body: { permissions }
      });
    } catch (error) {
      return { success: false, error: error.message || "Failed to update permissions" };
    }
  };

  // ==================== PROFILE FUNCTIONS ====================
  const getCurrentUser = async () => {
    if (user && getToken()) {
      return { success: true, user };
    }

    console.log("🟡 Fetching current user profile...");
    const res = await makeApiCall("/api/auth/profile");

    if (res.success && res.user) {
      console.log("✅ User profile loaded");
      updateUserState({
        ...user,
        ...res.user
      });
      await loadUserCommunities();
      await loadFollowing();
      await loadFollowers();
      await loadFollowRequests();
      await loadSuggestedUsers();
    } else {
      console.error("❌ Failed to load user profile:", res.error);
    }

    return res;
  };

  const updateUserProfile = async (data) => {
    try {
      console.log("🟡 Updating user profile...");
      let res;
      
      if (data instanceof FormData) {
        res = await makeApiCall("/api/auth/profile", {
          method: "PUT",
          body: data
        }, true);
      } else {
        res = await makeApiCall("/api/auth/profile", {
          method: "PUT",
          body: data
        });
      }
      
      if (res.success && res.user) {
        console.log("✅ Profile updated successfully");
        updateUserState({
          ...user,
          ...res.user
        });
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error updating profile:", error);
      return {
        success: false,
        error: error.message || "Failed to update profile"
      };
    }
  };

  const updateUserInterests = async (interests) => {
    try {
      const res = await updateUserProfile({ interests });
      return res;
    } catch (error) {
      console.error("❌ Error updating interests:", error);
      return {
        success: false,
        error: error.message || "Failed to update interests"
      };
    }
  };

  const checkUsernameAvailable = async (username) => {
    try {
      const res = await makeApiCall(`/api/auth/check-username?username=${encodeURIComponent(username)}`);
      return res;
    } catch (error) {
      console.error("❌ Error checking username:", error);
      return { success: false, error: error.message || "Failed to check username" };
    }
  };

  // ==================== COMMUNITY FUNCTIONS ====================
  const loadUserCommunities = async () => {
    try {
      console.log("🟡 Loading user communities...");
      const res = await makeApiCall("/api/community/my-groups");
      
      if (res.success) {
        const groups = res.groups || [];
        console.log(`✅ User communities loaded: ${groups.length} groups`);
        
        const createdGroups = groups.filter(g => g.isAdmin || g.createdBy?._id === user?._id);
        const memberGroups = groups.filter(g => g.isMember && !g.isAdmin);
        const pendingRequests = groups.filter(g => g.joinRequests?.some(r => r.status === 'pending'));
        
        setUserCommunities({
          createdGroups,
          memberGroups,
          pendingRequests
        });
      }
      return res;
    } catch (error) {
      console.error("❌ Error loading user communities:", error);
      return { success: false, error: error.message };
    }
  };

  const fetchGroups = async (filter = 'all', search = '', limit = 50) => {
    try {
      const params = new URLSearchParams();
      params.append('filter', filter);
      params.append('limit', limit.toString());
      if (search) params.append('search', search);
      
      const res = await makeApiCall(`/api/community/groups?${params.toString()}`);
      
      if (res.success) {
        console.log(`✅ Groups loaded: ${res.groups?.length || 0}`);
        return { success: true, groups: res.groups || [] };
      }
      return { success: false, error: res.error, groups: [] };
    } catch (error) {
      console.error("❌ Error fetching groups:", error);
      return { success: false, error: error.message, groups: [] };
    }
  };

  const createGroup = async (groupData, imageFile = null) => {
    try {
      if (!groupData?.name || !groupData?.description) {
        return {
          success: false,
          error: "Group name and description are required"
        };
      }
      
      const formData = new FormData();
      
      Object.keys(groupData).forEach(key => {
        if (groupData[key] !== null && groupData[key] !== undefined) {
          if (key === 'tags' && Array.isArray(groupData[key])) {
            formData.append(key, JSON.stringify(groupData[key]));
          } else {
            formData.append(key, groupData[key]);
          }
        }
      });
      
      if (imageFile) {
        formData.append('image', imageFile);
      }
      
      console.log("🟡 Creating group...");
      const res = await makeApiCall("/api/community/groups", {
        method: "POST",
        body: formData
      }, true);
      
      if (res.success && res.group) {
        console.log(`✅ Group created: ${res.group._id}`);
        await loadUserCommunities();
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error creating group:", error);
      return { success: false, error: error.message };
    }
  };

  const joinGroup = async (groupId, message = '') => {
    try {
      if (!groupId) {
        return {
          success: false,
          error: "Group ID is required"
        };
      }
      
      console.log(`🟡 Joining group ${groupId}...`);
      const res = await makeApiCall(`/api/community/groups/${groupId}/join`, {
        method: "POST",
        body: { message }
      });
      
      if (res.success) {
        console.log(`✅ Joined group ${groupId}`);
        await loadUserCommunities();
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error joining group:", error);
      return { success: false, error: error.message };
    }
  };

  const leaveGroup = async (groupId) => {
    try {
      if (!groupId) {
        return {
          success: false,
          error: "Group ID is required"
        };
      }
      
      console.log(`🟡 Leaving group ${groupId}...`);
      const res = await makeApiCall(`/api/community/groups/${groupId}/leave`, {
        method: "POST"
      });
      
      if (res.success) {
        console.log(`✅ Left group ${groupId}`);
        setUserCommunities(prev => ({
          ...prev,
          memberGroups: prev.memberGroups.filter(group => group._id !== groupId)
        }));
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error leaving group:", error);
      return { success: false, error: error.message };
    }
  };

  const deleteGroup = async (groupId) => {
    try {
      if (!groupId) {
        return {
          success: false,
          error: "Group ID is required"
        };
      }
      
      console.log(`🟡 Deleting group ${groupId}...`);
      const res = await makeApiCall(`/api/community/groups/${groupId}`, {
        method: "DELETE"
      });
      
      if (res.success) {
        console.log(`✅ Deleted group ${groupId}`);
        setUserCommunities(prev => ({
          ...prev,
          createdGroups: prev.createdGroups.filter(group => group._id !== groupId)
        }));
      }
      
      return res;
    } catch (error) {
      console.error("❌ Error deleting group:", error);
      return { success: false, error: error.message };
    }
  };

  // ==================== NOTIFICATION FUNCTIONS ====================
  const getNotificationCount = async () => {
    try {
      const res = await makeApiCall("/api/notifications/unread-count");
      if (res.success) {
        return { success: true, count: res.count || 0 };
      }
      return { success: false, count: 0, error: res.error };
    } catch (error) {
      console.error("❌ Error getting notification count:", error);
      return { success: false, count: 0, error: error.message };
    }
  };

  // ==================== SERVER CHECK FUNCTION ====================
  const checkServerStatus = async () => {
    try {
      console.log("🟡 Checking server status...");
      const response = await fetch(`${API_BASE_URL}/api/health`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        setServerOnline(true);
        console.log("✅ Server is online");
        return { success: true, online: true };
      } else {
        setServerOnline(false);
        console.error("❌ Server returned error status");
        return { success: false, online: false };
      }
    } catch (error) {
      setServerOnline(false);
      console.error(`❌ Cannot connect to server at ${API_BASE_URL}`);
      return { 
        success: false, 
        online: false,
        error: `Cannot connect to server at ${API_BASE_URL}`
      };
    }
  };

  // ==================== CLEAR ERROR FUNCTION ====================
  const clearError = () => {
    setError(null);
  };

  // ==================== HELPER FUNCTIONS ====================
  const isMutualFollow = (targetUserId) => {
    if (!targetUserId || !user) return false;
    
    const safeFollowing = Array.isArray(following) ? following : [];
    const safeFollowers = Array.isArray(followers) ? followers : [];
    
    const isFollowing = safeFollowing.some(u => {
      const userId = u._id || u.id || u.user?._id || u.followedUser?._id;
      return userId === targetUserId;
    });
    
    const isFollower = safeFollowers.some(f => {
      const followerId = f._id || f.id || f.follower?._id || f.user?._id;
      return followerId === targetUserId;
    });
    
    return isFollowing && isFollower;
  };

  const getFollowing = () => Array.isArray(following) ? following : [];
  const getFollowers = () => Array.isArray(followers) ? followers : [];
  const getFollowRequests = () => Array.isArray(followRequests) ? followRequests : [];
  const getSuggestedUsers = () => Array.isArray(suggestedUsers) ? suggestedUsers : [];

  // ==================== INITIALIZATION ====================
  useEffect(() => {
    const init = async () => {
      try {
        console.log("🟡 Initializing AuthContext...");
        await checkServerStatus();

        const cached = localStorage.getItem(STORAGE_KEYS.USER);
        if (cached) {
          try {
            const userData = JSON.parse(cached);
            updateUserState(userData);
            console.log("✅ Loaded user from cache");
          } catch (e) {
            localStorage.removeItem(STORAGE_KEYS.USER);
          }
        }

        const token = getToken();
        if (token) {
          console.log("🟡 Token found, fetching fresh user data...");
          const res = await getCurrentUser();
          
          if (!res.success) {
            console.error("❌ Failed to fetch user data, clearing token");
            updateUserState(null);
            setToken(null);
          }
        } else {
          console.log("🔐 No token found, user is not logged in");
        }
      } catch (e) {
        console.error("❌ Error during initialization:", e);
        updateUserState(null);
        setToken(null);
      } finally {
        setLoading(false);
        console.log("✅ AuthContext initialized");
      }
    };

    init();
  }, [updateUserState]);

  // ==================== PROVIDER VALUE ====================
  const value = {
    user,
    loading,
    redirectPath,
    userCommunities,
    serverOnline,
    error,
    
    // ✅ Follow system state
    following: getFollowing(),
    followers: getFollowers(),
    followRequests: getFollowRequests(),
    suggestedUsers: getSuggestedUsers(),
    
    // ✅ Follow system functions
    loadFollowing,
    loadFollowers,
    loadFollowRequests,
    loadSuggestedUsers,
    sendFollowRequest,
    acceptFollowRequest,
    rejectFollowRequest,
    unfollowUser,
    checkMutualFollow,
    loadAvailableUsersForMessages,
    getUserById,
    
    // ✅ Messages functions
    loadConversations,
    createConversation,
    loadConversationMessages,
    sendMessage,
    
    // ✅ Auth functions
    login,
    verifyTwoFactorLogin,
    setUser,
    register,
    registerOrganization,
    inviteTeamMember,
    verifyEmail,
    resendVerificationCode,
    socialAuth,
    logout,
    getSecondaryAdmins,
    createSecondaryAdmin,
    approveSecondaryAdminAccess,
    rejectSecondaryAdminAccess,
    deleteSecondaryAdmin,
    updateSecondaryAdminPermissions,
    
    // ✅ Profile functions
    getCurrentUser,
    updateUserProfile,
    updateUserInterests,
    checkUsernameAvailable,
    updateUser: (updates) => {
      if (!user) return { success: false };
      const updated = { ...user, ...updates };
      updateUserState(updated);
      return { success: true, user: updated };
    },
    
    // ✅ Community functions
    loadUserCommunities,
    fetchGroups,
    createGroup,
    joinGroup,
    leaveGroup,
    deleteGroup,
    
    // ✅ Post functions
    loadFeedPosts,
    createPost,
    likePost,
    savePost,
    commentOnPost,
    
    // ✅ Notification functions
    getNotificationCount,
    
    // ✅ Server functions
    checkServerStatus,
    
    // ✅ Utility functions
    clearError,
    getToken,
    clearRedirect: () => setRedirectPath(null),
    makeApiCall,
    adminGetDashboardStats,
    
    // ✅ Helper functions
    isLoggedIn: () => !!user && !!getToken(),
    getUserType: () => user?.userType || null,
    isOrganizer: () => user?.userType === 'organizer',
    isPlayer: () => user?.userType === 'player',
    isAdmin: () => isPrimaryAdminUser(user),
    isPrimaryAdmin: () => isPrimaryAdminUser(user),
    isSecondaryAdmin: () => isSecondaryAdminUser(user),
    hasAdminPermission: (permission) => hasAdminPermissionForUser(user, permission),
    isMutualFollow,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
